import { useState, useCallback } from "react";
import {
  VendorSearchItemEntity,
  VendorSearchPagination,
  SearchVendorsUseCase,
} from "../../domain";
import { VendorRepositoryImpl } from "../../data/repositories/VendorRepositoryImpl";

// Create repository instance (singleton)
const vendorRepository = new VendorRepositoryImpl();

// Create use case
const searchVendorsUseCase = new SearchVendorsUseCase(vendorRepository);

export interface UseVendorsReturn {
  vendors: VendorSearchItemEntity[];
  pagination: VendorSearchPagination | null;
  loading: boolean;
  error: string | null;

  searchVendors: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }) => Promise<void>;

  clearError: () => void;
  reset: () => void;
}

export const useVendors = (): UseVendorsReturn => {
  const [vendors, setVendors] = useState<VendorSearchItemEntity[]>([]);
  const [pagination, setPagination] = useState<VendorSearchPagination | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Search vendors with optional filters
   */
  const searchVendors = useCallback(
    async (
      params: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
      } = {}
    ): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const result = await searchVendorsUseCase.execute(params);

        if (!result.success) {
          throw new Error("Failed to search vendors");
        }

        setVendors(result.data);
        setPagination(result.pagination);
      } catch (error: any) {
        const errorMessage = error.message || "Failed to search vendors";
        setError(errorMessage);
        console.error("Failed to search vendors:", error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback((): void => {
    setVendors([]);
    setPagination(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    vendors,
    pagination,
    loading,
    error,
    searchVendors,
    clearError,
    reset,
  };
};
